import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Check, ChevronRight, ShoppingCart, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Motion";
import { API, money } from "@/lib/site";

export default function Kess3Builder() {
  const { type } = useParams();
  const isMaster = type === "master";
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState(false);
  const [sel, setSel] = useState(new Set());
  const { addItem, setOpen } = useCart();

  useEffect(() => {
    document.title = `KESS3 ${isMaster ? "Master" : "Slave"} Configurator | Regal Tuning`;
    setSel(new Set());
    axios.get(`${API}/kess3-config`).then((r) => setCfg(r.data)).catch(() => setErr(true));
  }, [type, isMaster]);

  if (!isMaster && type !== "slave") {
    return (
      <main className="grid min-h-[60vh] place-items-center pt-24">
        <Link to="/alientech-kess3" className="text-regal-amber underline">Back to Alientech</Link>
      </main>
    );
  }
  if (err || !cfg) {
    return <main className="container-regal pt-40 pb-24"><div className="aspect-video animate-pulse border border-line bg-surface" /></main>;
  }

  const options = isMaster ? cfg.master_options : cfg.slave_options;
  const ccy = cfg.currency || "GBP";
  const hardware = cfg.hardware_price ?? 500;
  const selArr = [...sel].sort((a, b) => a - b);
  const swTotal = selArr.reduce((a, i) => a + (options[i]?.price || 0), 0);
  const total = hardware + swTotal;
  const canAdd = sel.size > 0;

  const groups = [];
  options.forEach((o, i) => {
    const g = o.group || "Activations";
    let bucket = groups.find((x) => x.name === g);
    if (!bucket) { bucket = { name: g, items: [] }; groups.push(bucket); }
    bucket.items.push({ ...o, index: i });
  });

  const toggle = (i) => setSel((s) => {
    const n = new Set(s);
    if (n.has(i)) { n.delete(i); return n; }
    (options[i].conflicts || []).forEach((c) => n.delete(c));
    n.add(i);
    return n;
  });

  const add = () => {
    const lines = selArr.map((i) => `${options[i].name} — ${money(options[i].price, ccy)}`);
    const summary = [`KESS3 hardware — ${money(hardware, ccy)} (charged once)`, ...lines].join("\n");
    addItem(
      { slug: `build-your-kess3-${type}`, name: `Build Your KESS3 – ${isMaster ? "Master" : "Slave"}`, image: "/assets/brands/alientech.webp", currency: ccy, badge: isMaster ? "MASTER" : "SLAVE", category: "alientech" },
      1,
      { label: `Build Your KESS3 – ${isMaster ? "Master" : "Slave"}`, currency: ccy, total, options: selArr, summary }
    );
    setOpen(true);
  };

  return (
    <main data-testid="kess3-builder-page" className="pt-32 sm:pt-40 pb-24">
      <div className="container-regal">
        <nav className="mb-8 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" data-testid="breadcrumbs">
          <Link to="/tuning-tools" className="hover:text-regal-amber">Tuning Tools</Link>
          <ChevronRight size={12} />
          <Link to="/alientech-kess3" className="hover:text-regal-amber">Alientech</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">KESS3 {isMaster ? "Master" : "Slave"}</span>
        </nav>

        <Reveal>
          <p className="overline-label mb-3">Alientech Configurator</p>
          <h1 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink" data-testid="kess3-builder-title">
            KESS3 {isMaster ? "Master" : "Slave — Any Master"}
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-neutral-400">
            KESS3 hardware is charged once at {money(hardware, ccy)} — then add the software activations you need. Package options automatically replace their equivalent individual activations.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-10 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            <Reveal>
              <div className="flex items-center justify-between border border-line bg-white p-5" data-testid="kess3-hardware-row">
                <div className="flex items-center gap-4">
                  <span className="grid h-10 w-10 place-items-center bg-regal-amber text-white"><Check size={18} /></span>
                  <div>
                    <p className="font-display text-base font-bold uppercase tracking-wide text-ink">KESS3 Hardware</p>
                    <p className="text-sm text-neutral-400">Included once with every build</p>
                  </div>
                </div>
                <p className="font-display text-lg font-bold text-regal-amber">{money(hardware, ccy)}</p>
              </div>
            </Reveal>

            {groups.map((g) => (
              <div key={g.name} data-testid={`kess3-group-${g.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <Reveal><p className="overline-label mb-4">{g.name}</p></Reveal>
                <div className="space-y-3">
                  {g.items.map((o) => {
                    const active = sel.has(o.index);
                    const isPkg = o.name.toLowerCase().includes("package");
                    return (
                      <button
                        key={o.index}
                        type="button"
                        onClick={() => toggle(o.index)}
                        data-testid={`kess3-option-${o.index}`}
                        className={`flex w-full items-center justify-between gap-4 border p-4 sm:p-5 text-left transition-all duration-300 ${active ? "border-regal-amber bg-regal-amber/5" : "border-line bg-white hover:border-linebright"}`}
                      >
                        <span className="flex items-center gap-4 min-w-0">
                          <span className={`grid h-8 w-8 shrink-0 place-items-center border transition-colors ${active ? "border-regal-amber bg-regal-amber text-white" : "border-linebright text-transparent"}`}>
                            {active ? <Check size={15} /> : <Plus size={15} className="text-neutral-500" />}
                          </span>
                          <span className="text-sm font-medium text-ink leading-snug">
                            {o.name}
                            {isPkg && <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.14em] text-regal-amber">Package — best value</span>}
                          </span>
                        </span>
                        <span className="shrink-0 font-mono text-sm font-bold text-regal-amber">{money(o.price, ccy)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div>
            <div className="lg:sticky lg:top-28 border border-line bg-white p-6 sm:p-7 shadow-[0_16px_40px_rgba(16,24,40,0.08)]" data-testid="kess3-summary">
              <p className="overline-label mb-5">Your configuration</p>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between gap-4"><span className="text-neutral-400">KESS3 hardware (once)</span><span className="font-mono text-ink">{money(hardware, ccy)}</span></div>
                {selArr.map((i) => (
                  <div key={i} className="flex justify-between gap-4" data-testid={`kess3-summary-option-${i}`}>
                    <span className="text-neutral-400 leading-snug">{options[i].name}</span>
                    <span className="shrink-0 font-mono text-ink">{money(options[i].price, ccy)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 border-t border-line pt-5">
                <div className="flex items-end justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">Total</span>
                  <span className="font-display text-3xl font-extrabold text-regal-amber" data-testid="kess3-total">{money(total, ccy)}</span>
                </div>
              </div>
              <button
                onClick={add}
                disabled={!canAdd}
                data-testid="kess3-add-to-cart"
                className="mt-6 flex w-full items-center justify-center gap-2.5 bg-regal-amber py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                <ShoppingCart size={17} /> Add to Cart
              </button>
              <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                {sel.size === 0 ? "Select at least one activation" : ""}
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
